<template>
  <div class="heatmap-wrap">
    <div
      :style="style"
      class="heatmap"
    />
  </div>
</template>

<script>
import heatmap from 'heatmapjs';
// NOTE: heatmap.jsで生成されるcanvasが小数点以下を扱えておらず地図とズレが生じていたのでheatmapは単位をcmにする
const unitToCm = 100;

export default {
  data() {
    return {
      config: {
        container: null,
        radius: 4.5 * unitToCm,
        maxOpacity: 0.5,
        minOpacity: 0,
        blur: 0.75,
      },
      translate: {
        x: 0,
        y: 0,
      },
    };
  },
  computed: {
    windowRect: ({ $store }) => $store.state.appNavigator.windowRect,
    // NOTE: svgの値をhtmlの値にするための係数
    svgValueToHtmlValue() {
      let svgValueToHtmlValue;
      if (this.fitOrientation === 'width') {
        svgValueToHtmlValue = this.windowRect.width / this.currentBuilding.width;
      } else {
        svgValueToHtmlValue = this.windowRect.height / this.currentBuilding.depth;
      }
      return svgValueToHtmlValue / unitToCm;
    },
    // NOTE: svgが画面の左上から何pxずれているか
    svgRectLeft: (vm) => (vm.windowRect.width - vm.mapBaseDomRect.width) / 2,
    svgRectTop: (vm) => (vm.windowRect.height - vm.mapBaseDomRect.height) / 2,
    // NOTE: heatmapの移動距離
    heatmapDistanceX: (vm) => vm.mapTranslate.x * vm.svgValueToHtmlValue * unitToCm,
    //   NOTE: yは下を0として計算するためheightを足す
    heatmapDistanceY: (vm) => vm.mapBaseDomRect.height + vm.mapTranslate.y * vm.svgValueToHtmlValue * unitToCm,
    // NOTE: svgの(0, 0)を左下に変更しているので合わせる
    matchTheCoordinatesWithSvg: (vm) => vm.mapBaseDomRect.height * vm.mapScale,
    fitOrientation: ({ $store }) => $store.state.map.fitOrientation,
    currentBuildingId: ({ $store }) => $store.state.current.buildingId,
    currentBuilding: ({ $store, currentBuildingId }) => $store.getters['area/buildingFind'](currentBuildingId),
    heatmapPoints: (vm) => vm.$store.getters['heatmap/getHeatmapPoints'].map((point) => ({
      x: point.x * unitToCm,
      // NOTE: 左下から配置されるようにする
      y: (vm.currentBuilding.depth - point.y) * unitToCm,
      value: point.value,
    })),
    visualizationLayerType: ({ $store }) => $store.state.map.visualizationLayerType,
    canvas: {
      get() {
        return this.$store.state.heatmap.canvas;
      },
      set(elm) {
        this.$store.commit('heatmap/getCanvas', elm);
      },
    },
    instance: {
      get() {
        return this.$store.state.heatmap.instance;
      },
      set(heatmapElm) {
        this.$store.commit('heatmap/createInstance', heatmapElm);
      },
    },
    min: ({ $store }) => $store.state.heatmap.min,
    max: ({ $store }) => $store.state.heatmap.max,
    mapScale: ({ $store }) => $store.state.map.scale,
    mapTranslate: ({ $store }) => $store.state.map.translate,
    style: (vm) => ({
      width: `${Math.ceil(vm.currentBuilding.width * unitToCm)}px`,
      height: `${Math.ceil(vm.currentBuilding.depth * unitToCm)}px`,
      top: `${vm.$store.state.map.baseDomRect.y}px`,
      transform: `scale(${vm.svgValueToHtmlValue})`,
    }),
    mapBaseDomRect: ({ $store }) => $store.state.map.baseDomRect,
  },
  watch: {
    windowRect: {
      handler() {
        this.move();
      },
      deep: true,
    },
    currentBuilding: {
      handler() {
        this.remakeHeamap();
      },
      deep: true,
    },
    mapTranslate: {
      handler() {
        this.move();
      },
      deep: true,
    },
    visualizationLayerType() {
      this.setGradient();
    },
  },
  mounted() {
    this.$set(this.config, 'container', this.$el.querySelector('.heatmap'));
    this.instance = heatmap.create(this.config);
    this.canvas = this.$el.querySelector('canvas');
    this.setData();
    this.setGradient();
    this.move();
  },
  methods: {
    setData() {
      this.instance.setData({
        min: this.min,
        max: this.max,
        data: this.heatmapPoints,
      });
    },
    setGradient() {
      const newConfig = { ...this.config };

      if (this.visualizationLayerType === 'congestion') {
        newConfig.gradient = {
          0.25: 'rgb(0,0,255)',
          0.55: 'rgb(0,255,0)',
          0.85: 'yellow',
          1.0: 'rgb(255,0,0)',
        };
      } else if (this.visualizationLayerType === 'brightness') {
        newConfig.gradient = {
          1.00: '#ffe400',
          0.91: '#ffe617',
          0.82: '#ffe92e',
          0.73: '#ffeb46',
          0.64: '#ffee5d',
          0.55: '#fff074',
          0.45: '#fff38b',
          0.36: '#fff5a2',
          0.27: '#fff8b9',
          0.18: '#fffad1',
          0.09: '#fffde8',
          0.00: '#ffffff',
        };
      }

      this.instance.configure(newConfig);
      this.config = newConfig;
    },
    move() {
      let translateX = 0;
      let translateY = 0;

      if (this.fitOrientation === 'height') {
        translateX = this.svgRectLeft + this.heatmapDistanceX;
        translateY = this.heatmapDistanceY - this.matchTheCoordinatesWithSvg;
      } else {
        translateX = this.heatmapDistanceX;
        translateY = this.heatmapDistanceY + this.svgRectTop - this.matchTheCoordinatesWithSvg;
      }
      this.$set(this.translate, 'x', translateX);
      this.$set(this.translate, 'y', translateY);
    },
    remakeHeamap() {
      // NOTE: canvasのサイズを変更しても描画領域が変わらないので削除して作り直す
      this.instance._renderer.canvas.remove();

      setTimeout(() => {
        // NOTE: heatmapのサイズを再設定
        this.instance = heatmap.create(this.config);
        this.setData();
        this.move();
      }, 0);
    },
  },
};
</script>

<style>
  .heatmap-wrap {
    overflow: hidden;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    height: 100%;
    width: 100%;
    pointer-events: none;
  }
  .heatmap {
    transform-origin: 0 0;
  }
</style>



// WEBPACK FOOTER //
// heatmap.vue?33e0ee96